import { Injectable } from '@angular/core';

export interface SmtpError {
  code: number;
  enhancedCode: string;
}

@Injectable()
export class SmtpErrorsService {
  parse(errorString: string): SmtpError {
    if (!errorString) {
      return;
    }

    const match = errorString.match(/smtp;(\d{3})\s+([\d.]+)/);

    if (!match) {
      return;
    }

    return {
      code: Number(match[1]),
      enhancedCode: match[2],
    };
  }

  getErrorMessage(error: SmtpError): string {
    if (!error) {
      return 'Other SMTP error';
    }

    // Missing 'Auto-reply detected (e.g., Out of Office)', 'No recipient'
    // Detected via headers

    // Error code could not be recognized
    if (Number.isNaN(error.code)) {
      return 'Unknown error message';
    }

    // smtp;421 4.4.7 or smtp;450 4.4.7
    if ([421, 450].includes(error.code) && error.enhancedCode === '4.4.7') {
      return 'Delivery failed - Message timed out';
    }

    // smtp;554 5.5.0 (generic send failure)
    if (error.code === 554 && error.enhancedCode === '5.5.0') {
      return 'Failed Sending';
    }

    // smtp;452 4.2.2 or smtp;552 5.2.2
    if ([452, 552].includes(error.code) && ['4.2.2', '5.2.2'].includes(error.enhancedCode)) {
      return 'Mailbox full - Quota exceeded';
    }

    // smtp;450 4.2.0 or smtp;450 4.2.1
    if (error.code === 450 && ['4.2.0', '4.2.1'].includes(error.enhancedCode)) {
      return 'Mailbox unavailable - Try again later';
    }

    // smtp;451 4.5.0 or smtp;450 4.3.0 or smtp;450 4.5.0
    if ([451, 450].includes(error.code) && ['4.5.0', '4.3.0'].includes(error.enhancedCode)) {
      return 'Temporary local error - Try again later';
    }

    // smtp;550 5.7.1 or smtp;554 5.7.1
    if ([550, 554].includes(error.code) && error.enhancedCode === '5.7.1') {
      return 'Message rejected - Possible spam detected';
    }

    // smtp;550 5.1.1, smtp;550 5.1.2 or smtp;550 5.1.0
    if (error.code === 550 && ['5.1.1', '5.1.2', '5.1.0'].includes(error.enhancedCode)) {
      return 'Recipient address rejected - No such user';
    }

    // smtp;550 5.0.0 or smtp;540 5.0.0
    if ([550, 540].includes(error.code) && error.enhancedCode === '5.0.0') {
      return 'Unable to deliver message';
    }

    // smtp;5xx 5.x.x (any permanent failure not specifically categorized)
    if (error.code > 500 && error.enhancedCode.startsWith('5')) {
      return 'Permanent delivery error';
    }

    // Catch-all for any unmatched codes
    return 'Other SMTP error';
  }
}
